import React from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';

/**
 * One credit customer rate card: view, edit a row, delete a row, clear the card,
 * export it.
 *
 * The five cards differ only in their columns, so they are described once in COLUMNS
 * and rendered by the same component — mirroring the backend, where the same five are
 * described once in CreditCustomerRateAdminService::CARDS. Changing a card means
 * changing both, and only those two places.
 *
 * Editing happens inline. Opening a modal for four numbers is more ceremony than the
 * task deserves, and staff comparing a row against a rate sheet want to see the
 * neighbouring rows while they type.
 */

type RateType = 'fixed' | 'bulk' | 'flat' | 'multiplier' | 'special' | 'express';

type Column = {
    key: string;
    label: string;
    /** Blank means "no upper limit" rather than zero. */
    nullable?: boolean;
    /** Weight bands and unit types read as plain values; zone rates read as money. */
    money?: boolean;
    text?: boolean;
};

/**
 * The columns either side of the zone block, per card.
 *
 * The zone columns themselves are NOT listed here any more: how many a card has is now
 * configured per customer, so they are built from the zone list the backend returns.
 * Spelling them out again here is how the screen and the uploader would drift apart.
 */
const FRAME: Record<RateType, { before: Column[]; zoneLabel: (n: number) => string; zoneKey: (n: number) => string; after: Column[]; zoneless?: boolean }> = {
    fixed: {
        before: [{ key: 'weight_kg', label: 'Weight (KG)' }],
        zoneLabel: (n) => `Zone ${n}`,
        zoneKey: (n) => `zone_${n}_rate`,
        after: [],
    },
    bulk: {
        before: [
            { key: 'weight_from', label: 'From KG' },
            { key: 'weight_to', label: 'To KG' },
        ],
        zoneLabel: (n) => `Zone ${n} /kg`,
        zoneKey: (n) => `zone_${n}_rate_per_kg`,
        after: [],
    },
    flat: {
        before: [
            { key: 'weight_from', label: 'From KG' },
            { key: 'weight_to', label: 'To KG' },
        ],
        zoneLabel: (n) => `Zone ${n}`,
        zoneKey: (n) => `zone_${n}_rate`,
        after: [],
    },
    multiplier: {
        before: [
            { key: 'weight_from', label: 'From KG' },
            { key: 'weight_to', label: 'To KG' },
        ],
        zoneLabel: (n) => `Zone ${n} /kg`,
        zoneKey: (n) => `zone_${n}_rate_per_kg`,
        after: [],
    },
    // Express prices by destination CATEGORY, not by zone: Lagos Island / Lagos
    // Mainland / South West / Direct (Abuja, Rivers, Edo, Delta) / Nationwide. So it
    // has no zone block, and no entry on the Zone Configuration tab either.
    //
    // The 10.01 - 999999 band is not a real weight range: it holds the per-kg figures
    // charged on top of the 8.1-10kg band for anything over 10kg.
    express: {
        before: [
            { key: 'weight_from', label: 'Weight From' },
            { key: 'weight_to', label: 'Weight To' },
            { key: 'lagos_mainland_rate', label: 'Lagos Mainland', money: true },
            { key: 'lagos_island_rate', label: 'Lagos Island', money: true },
            { key: 'south_west_rate', label: 'South West', money: true },
            { key: 'direct_rate', label: 'Direct', money: true },
            { key: 'nationwide_rate', label: 'Nationwide', money: true },
            { key: 'additional_per_kg', label: 'Additional / KG', money: true, nullable: true },
        ],
        zoneLabel: (n) => `Zone ${n}`,
        zoneKey: (n) => `zone_${n}_rate`,
        after: [],
        zoneless: true,
    },
    // Banded by how many units are sent — "under 20 cartons 8,000, 20 and above
    // 7,800" — so a unit type has one row per band. `quantity_to` blank is the
    // open-ended top band, which is why it is nullable.
    special: {
        before: [
            { key: 'unit_type', label: 'Unit Type', text: true },
            { key: 'quantity_from', label: 'Qty From' },
            { key: 'quantity_to', label: 'Qty To', nullable: true },
        ],
        zoneLabel: (n) => `Zone ${n}`,
        zoneKey: (n) => `zone_${n}_rate`,
        after: [{ key: 'onforwarding_per_unit', label: 'Onforwarding / Unit', money: true }],
    },
};

/** The full column list for a card with this many zones, named by the zone's own label. */
const columnsFor = (type: RateType, zones: { zone_number: number; label?: string | null }[]): Column[] => {
    const frame = FRAME[type];

    // A card with no zones at all — express — is just its own columns.
    if (frame.zoneless) {
        return [...frame.before, ...frame.after];
    }

    const zoneColumns = zones.map((z) => ({
        key: frame.zoneKey(z.zone_number),
        // The configured name wins where there is one, so a six-zone card does not
        // present the reader with an unexplained "Zone 6".
        label: z.label ? `${frame.zoneLabel(z.zone_number)} — ${z.label}` : frame.zoneLabel(z.zone_number),
        money: true,
    }));

    return [...frame.before, ...zoneColumns, ...frame.after];
};

const TITLES: Record<RateType, string> = {
    express: 'Express Rates',
    fixed: 'Fixed Rates',
    bulk: 'Bulk Rates',
    flat: 'Flat Rates',
    multiplier: 'Multiplier Rates',
    special: 'Special Rates',
};

const apiError = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

const money = (v: any) =>
    `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type Props = {
    type: RateType;
    clientEmail: string;
    rows: any[];
    loader: boolean;
    onDownloadTemplate: () => void;
    onUpload: (e: any) => void;
    /** Re-fetch this card after a change. */
    onChanged: () => void;
    /** This card's zones, from the Zone Configuration tab. Defaults to four. */
    zones?: { zone_number: number; label?: string | null }[];
};

const DEFAULT_ZONES = [1, 2, 3, 4].map((n) => ({ zone_number: n, label: null as string | null }));

function CreditRateTable({ type, clientEmail, rows, loader, onDownloadTemplate, onUpload, onChanged, zones }: Props) {
    const columns = React.useMemo(
        () => columnsFor(type, zones && zones.length ? zones : DEFAULT_ZONES),
        [type, zones]
    );

    const [editingId, setEditingId] = React.useState<any>(null);
    const [draft, setDraft] = React.useState<any>({});
    const [busy, setBusy] = React.useState<string>('');

    // A blank row appended to the bottom of the table. Editing in place rather than in
    // a modal keeps the existing bands visible while a new one is typed, which is how
    // anyone actually fills in "20 and above" — by reading the row above it.
    const [adding, setAdding] = React.useState<boolean>(false);
    const [newRow, setNewRow] = React.useState<any>({});

    const startEdit = (row: any) => {
        setEditingId(row.id);
        // Seed from the row so an untouched field saves the value it already had,
        // rather than an empty string the backend would reject.
        setDraft(Object.fromEntries(columns.map((c) => [c.key, row[c.key] ?? ''])));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft({});
    };

    const saveEdit = async (row: any) => {
        setBusy(`save-${row.id}`);
        try {
            const res: any = await SuperAdminApis.updateCreditRateRow(type, row.id, draft);
            if (res?.data?.status) {
                toast.success(res.data.message || 'Rate updated');
                cancelEdit();
                onChanged();
            } else {
                toast.warn(res?.data?.message || 'Could not update this rate');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not update this rate'));
        } finally {
            setBusy('');
        }
    };

    const deleteRow = async (row: any) => {
        const describes = columns.slice(0, type === 'special' ? 1 : 2).map((c) => `${c.label} ${row[c.key]}`).join(', ');

        if (!window.confirm(`Delete the rate for ${describes}?\n\nThis affects pricing immediately.`)) return;

        setBusy(`del-${row.id}`);
        try {
            const res: any = await SuperAdminApis.deleteCreditRateRow(type, row.id);
            if (res?.data?.status) {
                toast.success(res.data.message);
                onChanged();
            } else {
                toast.warn(res?.data?.message || 'Could not delete this rate');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not delete this rate'));
        } finally {
            setBusy('');
        }
    };

    const deleteAll = async () => {
        // Typing the count is deliberate friction: this clears the customer's whole
        // card, and an accidental click here prices their shipments at nothing.
        const answer = window.prompt(
            `This deletes ALL ${rows.length} ${type} rate(s) for ${clientEmail}.\n\n` +
            `Their shipments in these bands cannot be priced until new rates are uploaded.\n\n` +
            `Type ${rows.length} to confirm.`
        );

        if (answer === null) return;

        if (answer.trim() !== String(rows.length)) {
            toast.info('Cancelled — the number did not match.');
            return;
        }

        setBusy('delete-all');
        try {
            const res: any = await SuperAdminApis.deleteAllCreditRates(type, clientEmail);
            if (res?.data?.status) {
                toast.success(res.data.message);
                onChanged();
            } else {
                toast.warn(res?.data?.message || 'Could not clear this rate card');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not clear this rate card'));
        } finally {
            setBusy('');
        }
    };

    const startAdd = () => {
        setEditingId(null);
        // Seed from the last row so a new band inherits the shape of the card and only
        // the figures that actually differ need typing.
        const last = rows[rows.length - 1];
        setNewRow(Object.fromEntries(columns.map((c) => [c.key, last && c.text ? last[c.key] ?? '' : ''])));
        setAdding(true);
    };

    const saveNew = async () => {
        setBusy('add');
        try {
            const res: any = await SuperAdminApis.createCreditRateRow(type, { ...newRow, client_id: clientEmail });
            if (res?.data?.status) {
                toast.success(res.data.message || 'Rate added');
                setAdding(false);
                setNewRow({});
                onChanged();
            } else {
                toast.warn(res?.data?.message || 'Could not add this rate');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not add this rate'));
        } finally {
            setBusy('');
        }
    };

    const exportRates = async () => {
        setBusy('export');
        try {
            const res: any = await SuperAdminApis.exportCreditRates(type, clientEmail);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type}-rates-${clientEmail}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiError(e, 'Could not export these rates'));
        } finally {
            setBusy('');
        }
    };

    return (
        <div>
            <div className='flex flex-wrap justify-between items-center gap-3 mb-4'>
                <h3 className='text-[16px] text-[#1C1C1C] font-semibold'>{TITLES[type]}</h3>

                <div className='flex flex-wrap gap-2'>
                    <button onClick={startAdd} disabled={adding || !!busy}
                        title="Add another band or row to this card"
                        className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 disabled:opacity-40">
                        + Add Row
                    </button>

                    <button onClick={onDownloadTemplate}
                        className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-600">
                        Download Template
                    </button>

                    {/* Exports what is configured now, in the template's own column
                        order — so this doubles as the way to bulk-edit: export, change
                        in Excel, upload back. */}
                    <button onClick={exportRates} disabled={!rows.length || !!busy}
                        title={rows.length ? 'Download the current rates as CSV' : 'Nothing to export yet'}
                        className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 disabled:opacity-40">
                        {busy === 'export' ? 'Exporting…' : 'Export Current'}
                    </button>

                    <label className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-[#c43939]">
                        {loader ? 'Uploading...' : 'Upload Excel'}
                        <input type="file" accept=".xlsx,.xls,.csv" onChange={onUpload} className="hidden" disabled={loader} />
                    </label>

                    <button onClick={deleteAll} disabled={!rows.length || !!busy}
                        title={rows.length ? 'Delete every rate on this card' : 'Nothing to delete'}
                        className="bg-white border border-[#E04343] text-[#E04343] px-4 py-2 rounded-md text-sm hover:bg-red-50 disabled:opacity-40">
                        {busy === 'delete-all' ? 'Deleting…' : 'Delete All'}
                    </button>
                </div>
            </div>

            {/* Uploading never removes rows, so a card can hold bands the current
                spreadsheet no longer has. Say so where someone would notice it. */}
            {rows.length > 0 && (
                <p className='text-[11px] text-gray-500 mb-2'>
                    Uploading a file adds and updates rows but never removes them. To replace a card
                    completely, use Delete All first.
                </p>
            )}

            <div className="relative overflow-x-auto bg-white rounded-lg">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} className="px-6 py-3">{c.label}</th>
                            ))}
                            <th className="px-6 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? (
                            rows.map((rate: any) => {
                                const editing = editingId === rate.id;

                                return (
                                    <tr key={rate.id} className="bg-white border-b hover:bg-gray-50">
                                        {columns.map((c) => (
                                            <td key={c.key} className="px-6 py-4">
                                                {editing ? (
                                                    <input
                                                        type={c.text ? 'text' : 'number'}
                                                        step={c.text ? undefined : '0.01'}
                                                        min={c.text ? undefined : '0'}
                                                        placeholder={c.nullable ? 'blank = and above' : undefined}
                                                        value={draft[c.key] ?? ''}
                                                        onChange={(e) => setDraft({ ...draft, [c.key]: e.target.value })}
                                                        className="border rounded px-2 py-1 w-28 text-sm"
                                                    />
                                                ) : c.nullable && (rate[c.key] === null || rate[c.key] === undefined || rate[c.key] === '') ? (
                                                    <span className="text-gray-500 italic">and above</span>
                                                ) : c.money ? (
                                                    money(rate[c.key])
                                                ) : (
                                                    <span className="font-medium">{rate[c.key]}</span>
                                                )}
                                            </td>
                                        ))}

                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            {editing ? (
                                                <>
                                                    <button onClick={() => saveEdit(rate)} disabled={!!busy}
                                                        className="text-green-700 font-medium hover:underline disabled:opacity-40">
                                                        {busy === `save-${rate.id}` ? 'Saving…' : 'Save'}
                                                    </button>
                                                    <button onClick={cancelEdit} disabled={!!busy}
                                                        className="ml-3 text-gray-500 hover:underline">
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => startEdit(rate)} disabled={!!busy}
                                                        className="text-blue-600 font-medium hover:underline disabled:opacity-40">
                                                        Edit
                                                    </button>
                                                    <button onClick={() => deleteRow(rate)} disabled={!!busy}
                                                        className="ml-3 text-[#E04343] font-medium hover:underline disabled:opacity-40">
                                                        {busy === `del-${rate.id}` ? 'Deleting…' : 'Delete'}
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={columns.length + 1} className="px-6 py-8 text-center text-gray-500">
                                    No {type} rates found. Upload an Excel file to add rates.
                                </td>
                            </tr>
                        )}
                        {adding && (
                            <tr className="bg-green-50 border-b">
                                {columns.map((c) => (
                                    <td key={c.key} className="px-6 py-4">
                                        <input
                                            type={c.text ? 'text' : 'number'}
                                            step={c.text ? undefined : '0.01'}
                                            min={c.text ? undefined : '0'}
                                            placeholder={c.nullable ? 'blank = and above' : c.label}
                                            value={newRow[c.key] ?? ''}
                                            onChange={(e) => setNewRow({ ...newRow, [c.key]: e.target.value })}
                                            className="border rounded px-2 py-1 w-28 text-sm"
                                        />
                                    </td>
                                ))}
                                <td className="px-6 py-4 text-right whitespace-nowrap">
                                    <button onClick={saveNew} disabled={!!busy}
                                        className="text-green-700 font-medium hover:underline disabled:opacity-40">
                                        {busy === 'add' ? 'Adding…' : 'Add'}
                                    </button>
                                    <button onClick={() => { setAdding(false); setNewRow({}); }} disabled={!!busy}
                                        className="ml-3 text-gray-500 hover:underline">Cancel</button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default CreditRateTable;
